import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CometChatUIKit,
  CometChatUIKitLoginListener,
} from "@cometchat/chat-uikit-react";
import { COMETCHAT_CONSTANTS } from "../../AppConstants";
import "../../styles/CometChatLogin/DBMGate.css";

/**
 * dbm. chat Gate screen.
 *
 * Identity comes from the DBM Partner Portal, but is no longer trusted
 * outright — the portal only tells us WHICH email is logged in (a hint,
 * not proof), and we independently verify that email controls its own
 * inbox via a one-time OTP code, the first time each browser is seen.
 * After that, a signed token in localStorage (NOT a cookie — this app is
 * embedded cross-origin, and third-party cookies are increasingly
 * blocked/partitioned; localStorage read by our own script isn't) lets
 * every later visit skip straight through, fully automatic again.
 *
 * Entry points for the email hint:
 *   1. Pop-out / direct link:  chat.dbmpartnerportal.com.au/login?email=...
 *   2. Embedded in Softr:      the portal page posts
 *        { type: "DBM_CHAT_LOGIN", email }
 *      to the iframe via postMessage.
 *
 * Getting a hint no longer sends the code by itself — it just moves to a
 * "hint-ready" screen with a Send button (see requestOtp's call sites).
 * The stored-session fast path above is meant to make repeat OTP rare in
 * the first place, but on a browser/embedding where that storage doesn't
 * persist (observed with some third-party-iframe storage restrictions),
 * auto-sending on every single load meant a real email going out every
 * time with no user intent behind it at all. Requiring a click doesn't
 * fix that underlying persistence problem, but it does mean nobody gets
 * silently emailed for a page load they didn't ask to sign in from.
 */

const REQUEST_OTP_URL =
  process.env.REACT_APP_REQUEST_OTP_URL ||
  "/.netlify/functions/dbm-request-otp";
const VERIFY_OTP_URL =
  process.env.REACT_APP_VERIFY_OTP_URL ||
  "/.netlify/functions/dbm-verify-otp";
const VERIFY_SESSION_URL =
  process.env.REACT_APP_VERIFY_SESSION_URL ||
  "/.netlify/functions/dbm-verify-session";
const DEV_TEST_LOGIN_URL =
  process.env.REACT_APP_DEV_TEST_LOGIN_URL ||
  "/.netlify/functions/dbm-dev-test-login";

/** Local/dev-only escape hatch — never true in the real deploy. */
const DEV_TEST_LOGIN_ENABLED =
  process.env.REACT_APP_DBM_DEV_ALLOW_TEST_LOGIN === "true";

const LOCAL_SESSION_KEY = "dbm-local-session-token";

const PORTAL_URL =
  process.env.REACT_APP_PORTAL_URL || "https://dbmpartnerportal.com.au";

const ALLOWED_PARENT_ORIGINS = (
  process.env.REACT_APP_ALLOWED_PARENT_ORIGINS ||
  "https://dbmpartnerportal.com.au,https://www.dbmpartnerportal.com.au"
)
  .split(",")
  .map((o) => o.trim());

/** How long to wait for an email hint before showing the fallback. */
const GRACE_MS = 6000;

/** True only when actually embedded in a cross-origin frame (the portal
 * page) — the pop-out/direct-link mode runs as its own top-level page,
 * where there's no third-party storage restriction to work around in
 * the first place. A cross-origin frame throws just reading `window.top`
 * in some browsers, which itself is proof of being framed. */
function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * Asks the browser for unpartitioned access to this origin's own storage
 * — the Storage Access API, built for exactly this "legitimate embedded
 * content needs to remember who you are" case. Must run inside a real
 * user gesture (a click) in most implementations, which is why this is
 * only ever called from the Send-code button, never automatically on
 * load. Returns false — never throws — for an unsupported browser, a
 * denied prompt, or any other failure; every caller treats that as "carry
 * on with the normal flow," not an error.
 */
async function tryGrantStorageAccess(): Promise<boolean> {
  try {
    if (typeof document.requestStorageAccess !== "function") return false;
    await document.requestStorageAccess();
    return true;
  } catch {
    return false;
  }
}

type GateState =
  | "checking-session"
  | "connecting"
  | "hint-ready"
  | "sending-otp"
  | "otp-entry"
  | "verifying-otp"
  | "opening"
  | "nosession"
  | "error";

const DBMGate = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<GateState>("checking-session");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [code, setCode] = useState("");
  const [testLoginBusy, setTestLoginBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const startedRef = useRef(false);
  const hintHandledRef = useRef(false);

  const hasCredentials = () => {
    const appID = localStorage.getItem("appId") || COMETCHAT_CONSTANTS.APP_ID;
    const region = localStorage.getItem("region") || COMETCHAT_CONSTANTS.REGION;
    return appID !== "" && region !== "";
  };

  const finishLogin = useCallback(
    async (authToken: string) => {
      setState("opening");
      await CometChatUIKit.loginWithAuthToken(authToken);
      navigate("/home", { replace: true });
    },
    [navigate]
  );

  /** Fast path: an already-verified browser skips OTP entirely. */
  const tryStoredSession = useCallback(async (): Promise<boolean> => {
    const token = localStorage.getItem(LOCAL_SESSION_KEY);
    if (!token) return false;
    try {
      const res = await fetch(VERIFY_SESSION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Session check failed");
      await finishLogin(data.authToken);
      return true;
    } catch {
      localStorage.removeItem(LOCAL_SESSION_KEY);
      return false;
    }
  }, [finishLogin]);

  /** Sends the OTP for a hinted email and moves to the entry screen. */
  const requestOtp = useCallback(async (targetEmail: string) => {
    setEmail(targetEmail);
    setState("sending-otp");
    try {
      const res = await fetch(REQUEST_OTP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: targetEmail }),
      });
      const data = await res.json();
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error || "Couldn't send a code");
      }
      setState("otp-entry");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Couldn't send a code");
      setState("error");
    }
  }, []);

  /**
   * The Send-code button's actual handler. Tries to fix storage access
   * FIRST, using this click as the required user gesture — if that's
   * granted, the earlier (silent, on-load) tryStoredSession failure might
   * simply have been unable to see a token that was there all along, now
   * newly visible. Re-checking before sending anything means a browser
   * that just needed this one permission can skip straight to signed-in
   * with no code/email involved at all, rather than sending one anyway
   * out of habit. Only falls through to a real OTP send if that still
   * comes up empty — same as today for anyone this doesn't help.
   */
  const sendCode = useCallback(
    async (targetEmail: string) => {
      if (isEmbedded()) {
        const granted = await tryGrantStorageAccess();
        if (granted && (await tryStoredSession())) return;
      }
      requestOtp(targetEmail);
    },
    [tryStoredSession, requestOtp]
  );

  const verifyOtp = useCallback(async () => {
    if (!code.trim()) return;
    setState("verifying-otp");
    try {
      const res = await fetch(VERIFY_OTP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "That code didn't work");
      if (data.localSessionToken) {
        localStorage.setItem(LOCAL_SESSION_KEY, data.localSessionToken);
      }
      await finishLogin(data.authToken);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "That code didn't work");
      setState("otp-entry"); // let them retry without losing the email
    }
  }, [code, email, finishLogin]);

  /** Local/dev-only escape hatch — signs in as a fixed test identity,
   * skipping the sheet check, Supabase, and email OTP entirely. */
  const signInAsTestUser = useCallback(async () => {
    setTestLoginBusy(true);
    try {
      const res = await fetch(DEV_TEST_LOGIN_URL, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Test login failed");
      await finishLogin(data.authToken);
    } catch (err) {
      setTestLoginBusy(false);
      setErrorMsg(err instanceof Error ? err.message : "Test login failed");
      setState("error");
    }
  }, [finishLogin]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    (async () => {
      if (CometChatUIKitLoginListener.getLoggedInUser()) {
        navigate("/home", { replace: true });
        return;
      }
      if (!hasCredentials()) {
        navigate("/credentials");
        return;
      }

      // Fast path: already verified on this browser.
      const restored = await tryStoredSession();
      if (restored) return;

      setState("connecting");

      // 1) Pop-out / direct-link — email hint via query param.
      const params = new URLSearchParams(window.location.search);
      const hintedEmail = params.get("email");
      if (hintedEmail) {
        window.history.replaceState({}, "", window.location.pathname);
        hintHandledRef.current = true;
        setEmail(hintedEmail);
        setState("hint-ready");
        return;
      }

      // 2) Embedded — email hint via postMessage from the portal page.
      // Guarded to fire at most once per mount — the portal page
      // re-posting this (e.g. on its own internal navigation) shouldn't
      // pull the user back to the hint-ready screen after they've already
      // moved past it.
      const onMessage = (event: MessageEvent) => {
        if (!ALLOWED_PARENT_ORIGINS.includes(event.origin)) return;
        const data = event.data;
        if (data && data.type === "DBM_CHAT_LOGIN" && data.email && !hintHandledRef.current) {
          hintHandledRef.current = true;
          setEmail(data.email);
          setState("hint-ready");
        }
      };
      window.addEventListener("message", onMessage);

      if (window.parent !== window) {
        ALLOWED_PARENT_ORIGINS.forEach((origin) => {
          window.parent.postMessage({ type: "DBM_CHAT_READY" }, origin);
        });
      }

      // 3) No hint arrived: show fallback.
      const timer = window.setTimeout(() => {
        setState((s) => (s === "connecting" ? "nosession" : s));
      }, GRACE_MS);

      return () => {
        window.removeEventListener("message", onMessage);
        window.clearTimeout(timer);
      };
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const busy = state === "checking-session" || state === "connecting" || state === "opening";

  return (
    <div className="dbm-gate">
      <div className="dbm-gate__brandside">
        <div className="dbm-gate__brandtop">DBM Partner Portal · Secure Messaging</div>

        <div className="dbm-gate__hero">
          <h1 className="dbm-gate__wordmark">
            db<span>m</span><span>.</span><span className="dbm-gate__chat">chat</span>
          </h1>
          <p>One chat for every user on the DBM Partner Portal.</p>
        </div>

        <div className="dbm-gate__brandfoot">
          <div className="cell"><b>One login</b><span>Via your portal account</span></div>
          <div className="cell"><b>Verified once</b><span>Per device, not per visit</span></div>
          <div className="cell"><b>Encrypted</b><span>In transit &amp; at rest</span></div>
        </div>
      </div>

      <div className="dbm-gate__formside">
      <div className="dbm-gate__card">
        {busy && (
          <>
            <h1 className="dbm-gate__title">
              Connecting<span className="dbm-gate__ellip" />
            </h1>
            <p className="dbm-gate__sub">
              Picking up your DBM Partner Portal session and signing you in.
            </p>
          </>
        )}

        {state === "hint-ready" && (
          <>
            <h1 className="dbm-gate__title">
              Ready to sign in<span className="dbm-gate__dot">.</span>
            </h1>
            <p className="dbm-gate__sub">
              Signed in to the portal as <b>{email}</b>. Send a one-time code to
              confirm it's you — just once per device.
            </p>
            <button
              className="dbm-gate__btn"
              disabled={sendingCode}
              onClick={() => {
                setSendingCode(true);
                void sendCode(email).finally(() => setSendingCode(false));
              }}
            >
              <span>{sendingCode ? "One moment…" : "Send code"}</span>
              {!sendingCode && <span aria-hidden="true">→</span>}
            </button>
          </>
        )}

        {state === "sending-otp" && (
          <>
            <h1 className="dbm-gate__title">
              Sending a code<span className="dbm-gate__ellip" />
            </h1>
            <p className="dbm-gate__sub">Confirming it's really you — one time per device.</p>
          </>
        )}

        {(state === "otp-entry" || state === "verifying-otp") && (
          <>
            <h1 className="dbm-gate__title">
              Check your email<span className="dbm-gate__dot">.</span>
            </h1>
            <p className="dbm-gate__sub">
              We sent a 6-digit code to <b>{email}</b>. This only happens once on this device.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                verifyOtp();
              }}
            >
              <input
                className="dbm-gate__otpinput"
                type="text"
                inputMode="numeric"
                autoFocus
                maxLength={6}
                placeholder="000000"
                value={code}
                disabled={state === "verifying-otp"}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              />
              <button
                type="submit"
                className="dbm-gate__btn"
                disabled={state === "verifying-otp" || code.trim().length < 6}
              >
                <span>{state === "verifying-otp" ? "Verifying…" : "verify & continue"}</span>
                {state !== "verifying-otp" && <span aria-hidden="true">→</span>}
              </button>
            </form>
            <button
              className="dbm-gate__resend"
              disabled={state === "verifying-otp" || resendCooldown}
              onClick={() => {
                setResendCooldown(true);
                window.setTimeout(() => setResendCooldown(false), 30_000);
                requestOtp(email);
              }}
            >
              {resendCooldown ? "Code sent — check your email" : "Didn't get it? Send again"}
            </button>
          </>
        )}

        {state === "nosession" && (
          <>
            <h1 className="dbm-gate__title">
              No portal session<span className="dbm-gate__dot">.</span>
            </h1>
            <p className="dbm-gate__sub">
              Chat runs on your DBM Partner Portal account. Sign in to the
              portal and you'll land straight back here.
            </p>
            <a className="dbm-gate__btn" href={PORTAL_URL}>
              <span>open the partner portal</span>
              <span aria-hidden="true">→</span>
            </a>
            {DEV_TEST_LOGIN_ENABLED && (
              <button
                className="dbm-gate__resend"
                disabled={testLoginBusy}
                onClick={signInAsTestUser}
              >
                {testLoginBusy ? "Signing in…" : "Dev: sign in as test user"}
              </button>
            )}
          </>
        )}

        {state === "error" && (
          <>
            <h1 className="dbm-gate__title">
              Couldn't sign you in<span className="dbm-gate__dot">.</span>
            </h1>
            <p className="dbm-gate__sub">
              Something went wrong{errorMsg ? ` (${errorMsg})` : ""}. Head back
              to the portal and open chat again.
            </p>
            <a className="dbm-gate__btn" href={PORTAL_URL}>
              <span>back to the partner portal</span>
              <span aria-hidden="true">→</span>
            </a>
            {DEV_TEST_LOGIN_ENABLED && (
              <button
                className="dbm-gate__resend"
                disabled={testLoginBusy}
                onClick={signInAsTestUser}
              >
                {testLoginBusy ? "Signing in…" : "Dev: sign in as test user"}
              </button>
            )}
          </>
        )}

        <div className="dbm-gate__foot">
          <span>chat.dbmpartnerportal.com.au</span>
          <span>NSW AU</span>
        </div>
      </div>
      </div>
    </div>
  );
};

export default DBMGate;
